import {Component, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {ProductCardSyncDialogData, SyncProductCard} from "./types/product-card-sync-modal.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {ProductCardData} from "../../../types/product-card-data.types";
import {ProductCardSyncService} from "./services/product-card-sync.service";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {tabsData} from "./configs/product-card-sync-tabs";
import {CarouselComponent} from "../../../../../../shared/components/ui/carousel/carousel.component";
import {PcSyncProductCardComponent} from "./components/pc-sync-product-card/pc-sync-product-card.component";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";
import {MatButton} from "@angular/material/button";

@Component({
  selector: 'app-product-card-sync-modal',
  imports: [
    BaseModalComponent,
    MatTab,
    MatTabGroup,
    CarouselComponent,
    PcSyncProductCardComponent,
    LoaderComponent,
    MatButton
  ],
  templateUrl: './product-card-sync-modal.component.html',
  styleUrl: './product-card-sync-modal.component.scss'
})
export class ProductCardSyncModalComponent extends BaseModalComponent {
  private productCardSyncDialogData = inject<ProductCardSyncDialogData>(MAT_DIALOG_DATA);
  private productCardSyncService: ProductCardSyncService = inject(ProductCardSyncService);

  public readonly tabsData = tabsData;
  public syncProductCards = signal<Array<ProductCardData>>([]);
  public productCards = signal<Array<ProductCardData>>([]);
  public currentParamKey = signal<string>('');
  public currentIndex: number = 0;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.modalStatesService.openModal('productCardSync');
    this.loadingService.setIsLoadingState(true);
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal('productCardSync');
  }

  public getProductCardsDataByTab(tabKey: string): Array<ProductCardData> {
    return tabKey === 'all' ?
      this.productCards().filter(i => i.id !== this.productCardSyncDialogData.productCardId) :
      this.syncProductCards();
  }

  public async handleBtnClick(productCardData: ProductCardData, btnKey: string): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const handlers: Record<string, (productCardData: ProductCardData) => Promise<void>> = {
      attach: () => this.attachProductCard(productCardData),
      detach: () => this.detachProductCard(productCardData)
    };

    await handlers[btnKey]?.(productCardData);
  }

  public handleIndexChange(index: number): void {
    this.currentIndex = index;
  }

  private async initModal(): Promise<void> {
    const { productCardId, linkType, ...fetchProductCardsListParams } = this.productCardSyncDialogData;
    this.currentParamKey.set(Object.keys(fetchProductCardsListParams).find(() => fetchProductCardsListParams) || '');
    await this.productCardSyncService.fetchProductCardsList(this.productCardSyncDialogData)
      .then((data) => this.productCards.set(data));
    await this.productCardSyncService.fetchSyncProductCardsList({
      productCardId: productCardId,
      linkType: linkType
    })
      .then((data) => this.syncProductCards.set(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async attachProductCard(productCardData: ProductCardData): Promise<void> {
    const syncProductCard: SyncProductCard = {
      firstProductCardId: this.productCardSyncDialogData.productCardId,
      secondProductCardId: productCardData.id,
      linkType: this.productCardSyncDialogData.linkType
    };

    await this.productCardSyncService.attachProductCard(syncProductCard)
      .then(() => {
        this.syncProductCards.update(cards => [...cards, productCardData]);
        this.productCards.update(cards => cards.filter(card => card.id !== productCardData.id));
      })
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async detachProductCard(productCardData: ProductCardData): Promise<void> {
    const syncProductCard: SyncProductCard = {
      firstProductCardId: this.productCardSyncDialogData.productCardId,
      secondProductCardId: productCardData.id,
      linkType: this.productCardSyncDialogData.linkType
    };

    await this.productCardSyncService.detachProductCard(syncProductCard)
      .then(() => {
        this.productCards.update(cards => [...cards, productCardData]);
        this.syncProductCards.update(cards => cards.filter(card => card.id !== productCardData.id));
      })
      .finally(() => this.loadingService.setIsLoadingState(false));
  }
}
